import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { TextField } from '../ui/TextField';
import { TextArea } from '../ui/TextArea';
import { Badge } from '../ui/badge';
import { 
  FeatherX,
  FeatherSave,
  FeatherPlus,
  FeatherTag,
  FeatherUser,
  FeatherGlobe,
  FeatherMail,
  FeatherPhone,
  FeatherFileText,
  FeatherClock,
  FeatherUsers,
  FeatherGrid,
  FeatherTrendingUp,
  FeatherCheck,
  FeatherChevronLeft,
  FeatherChevronRight
} from '../ui/icons';
import api from '../../services/api';
import { useMasterDataArray } from '../../hooks/useMasterData';
import { useAuth } from '../../context/AuthContext';
import './PartnerCompanyForm.css';

const PartnerCompanyForm = ({ company, onSuccess, onCancel, onSwitchToQuick, users }) => {
  const { user } = useAuth();
  
  const [formData, setFormData] = useState({
    // Section 1: Company Profile
    name: '',
    website: '',
    industry: '',
    years_in_business: '',
    company_size: '',
    office_locations: [],
    timezone: '',
    working_hours: '',
    language_proficiency: [],
    certifications: [],
    portfolio_links: [],
    
    // Section 2: Key Contacts
    primary_contact: { name: '', role: '', email: '', phone: '' },
    decision_maker: { name: '', role: '', email: '' },
    communication_style: '',
    preferred_tools: [],
    availability_for_calls: '',
    
    // Section 3: Technical Capabilities
    technology_stack: [],
    skill_categories: [],
    experience_matrix: {},
    specializations: [],
    delivery_model: [],
    
    // Section 4: Resource Availability
    team_size: '',
    current_capacity: '',
    scalability: '',
    response_time: '',
    
    // Section 5: Project Delivery Capability
    project_types: [],
    size_range: [],
    methodology: [],
    qa_processes: [],
    minimum_engagement: '',
    travel_policy: '',
    
    // Section 6: Financial Terms
    rate_ranges: {},
    contract_preferences: [],
    payment_terms: '',
    invoicing_format: '',
    currency: 'USD',
    tax_id: '',
    
    // Legacy fields
    contact_persons: [],
    phone: '',
    email: '',
    location: '',
    initial_notes: '',
    willing_to_partner: true,

    available_skills: [],
    future_skills: [],
    assigned_to: user?.id || '', // Auto-assign to current user for new companies
    tags: []
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [nameValidation, setNameValidation] = useState({ isValid: true, message: '' });
  const [checkingName, setCheckingName] = useState(false);
  const [similarNames, setSimilarNames] = useState([]);
  const [tags, setTags] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);

  // Prevent form submission on Enter key when modal is open
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (e.key === 'Enter' && e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA' && e.target.tagName !== 'SELECT') {
        console.log('Global keydown preventing Enter on:', e.target);
        e.preventDefault();
        e.stopPropagation();
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown, true);
    
    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown, true);
    };
  }, []);
  const [expandedSections, setExpandedSections] = useState({
    companyProfile: true,
    keyContacts: false,
    technicalCapabilities: false,
    resourceAvailability: false,
    projectDelivery: false,
    financialTerms: false,
    partnershipStatus: false,
    tags: false,
    notes: false
  });

  // Master data hooks
  const { values: companySizeOptions, loading: companySizeLoading } = useMasterDataArray('company_size');
  const { values: communicationStyleOptions, loading: communicationStyleLoading } = useMasterDataArray('communication_style');
  const { values: capacityOptions, loading: capacityLoading } = useMasterDataArray('capacity_options');
  const { values: responseTimeOptions, loading: responseTimeLoading } = useMasterDataArray('response_time');
  const { values: currencyOptions, loading: currencyLoading } = useMasterDataArray('currency');
  const { values: technologyOptions, loading: technologyLoading } = useMasterDataArray('technology_stack');
  const { values: skillCategoryOptions, loading: skillCategoryLoading } = useMasterDataArray('skill_categories');
  const { values: projectTypeOptions, loading: projectTypeLoading } = useMasterDataArray('project_types');
  const { values: sizeRangeOptions, loading: sizeRangeLoading } = useMasterDataArray('size_range');
  const { values: methodologyOptions, loading: methodologyLoading } = useMasterDataArray('methodology');
  const { values: qaProcessOptions, loading: qaProcessLoading } = useMasterDataArray('qa_processes');
  const { values: deliveryModelOptions, loading: deliveryModelLoading } = useMasterDataArray('delivery_model');
  const { values: contractPreferenceOptions, loading: contractPreferenceLoading } = useMasterDataArray('contract_preferences');
  const { values: toolOptions, loading: toolLoading } = useMasterDataArray('communication_tools');

  const isEditing = !!company;

  useEffect(() => {
    fetchTags();
    if (company) {
      setFormData({
        // Section 1: Company Profile
        name: company.name || '',
        website: company.website || '',
        industry: company.industry || '',
        years_in_business: company.years_in_business || '',
        company_size: company.company_size || '',
        office_locations: company.office_locations || [],
        timezone: company.timezone || '',
        working_hours: company.working_hours || '',
        language_proficiency: company.language_proficiency || [],
        certifications: company.certifications || [],
        portfolio_links: company.portfolio_links || [],
        
        // Section 2: Key Contacts
        primary_contact: company.primary_contact || { name: '', role: '', email: '', phone: '' },
        decision_maker: company.decision_maker || { name: '', role: '', email: '' },
        communication_style: company.communication_style || '',
        preferred_tools: company.preferred_tools || [],
        availability_for_calls: company.availability_for_calls || '',
        
        // Section 3: Technical Capabilities
        technology_stack: company.technology_stack || [],
        skill_categories: company.skill_categories || [],
        experience_matrix: company.experience_matrix || {},
        specializations: company.specializations || [],
        delivery_model: company.delivery_model || [],
        
        // Section 4: Resource Availability
        team_size: company.team_size || '',
        current_capacity: company.current_capacity || '',
        scalability: company.scalability || '',
        response_time: company.response_time || '',
        
        // Section 5: Project Delivery Capability
        project_types: company.project_types || [],
        size_range: company.size_range || [],
        methodology: company.methodology || [],
        qa_processes: company.qa_processes || [],
        minimum_engagement: company.minimum_engagement || '',
        travel_policy: company.travel_policy || '',
        
        // Section 6: Financial Terms
        rate_ranges: company.rate_ranges || {},
        contract_preferences: company.contract_preferences || [],
        payment_terms: company.payment_terms || '',
        invoicing_format: company.invoicing_format || '',
        currency: company.currency || 'USD',
        tax_id: company.tax_id || '',
        
        // Legacy fields
        contact_persons: company.contact_persons || [],
        phone: company.phone || '',
        email: company.email || '',
        location: company.location || '',
        initial_notes: company.initial_notes || '',
        willing_to_partner: company.willing_to_partner !== false,

        available_skills: company.available_skills || [],
        future_skills: company.future_skills || [],
        assigned_to: company.assigned_to || '',
        tags: company.partner_tag_relations?.map(rel => rel.partner_tags.id) || []
      });
    }
  }, [company]);

  // Auto-assign current user for new companies only
  useEffect(() => {
    if (!company && user?.id && !formData.assigned_to) {
      setFormData(prev => ({
        ...prev,
        assigned_to: user.id
      }));
    }
  }, [user?.id, company, formData.assigned_to]);

  const fetchTags = async () => {
    try {
      console.log('Fetching tags...');
      const response = await api.get('/api/partner-companies/tags/all');
      console.log('Tags response:', response.data);
      console.log('Tags count:', response.data?.length || 0);
      setAvailableTags(response.data || []);
    } catch (err) {
      console.error('Error fetching tags:', err);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasUnsavedChanges(true);
    
    // Clear name validation when user starts typing
    if (field === 'name') {
      setNameValidation({ isValid: true, message: '' });
      setSimilarNames([]);
    }
  };

  // Debounced function to check for duplicate company names and find similar names
  const checkDuplicateName = React.useCallback(
    React.useMemo(() => {
      let timeoutId;
      return (name) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(async () => {
          if (!name || name.trim().length < 2) {
            setNameValidation({ isValid: true, message: '' });
            setSimilarNames([]);
            return;
          }
          
          // Skip validation if editing and name hasn't changed
          if (company && name.trim().toLowerCase() === company.name.toLowerCase()) {
            setNameValidation({ isValid: true, message: 'Current company name.' });
            setSimilarNames([]);
            return;
          }
          
          setCheckingName(true);
          try {
            const api = require('../../services/api').default;
            
            // Try multiple search strategies to get better results
            const searchTerms = [];
            const inputWords = name.trim().split(/\s+/).filter(word => word.length >= 3);
            
            // Add the full search term
            searchTerms.push(name.trim());
            
            // Add individual words as search terms
            inputWords.forEach(word => {
              searchTerms.push(word);
            });
            
            console.log('Search terms to try:', searchTerms);
            
            // Get companies using the first word (most likely to match)
            const primarySearchTerm = inputWords.length > 0 ? inputWords[0] : name.trim();
            const response = await api.get(`/api/partner-companies?search=${encodeURIComponent(primarySearchTerm)}&limit=20`);
            
            // Debug: Log the API response
            console.log('API Response for search:', primarySearchTerm, response.data);
            console.log('Companies returned:', response.data.companies?.length || 0);
            console.log('All companies:', response.data.companies?.map(c => c.name) || []);
            
            const existingCompany = response.data.companies?.find(company => 
              company.name.toLowerCase() === name.trim().toLowerCase()
            );
            
            if (existingCompany) {
              setNameValidation({ 
                isValid: false, 
                message: `A company with the name "${existingCompany.name}" already exists.` 
              });
              setSimilarNames([]);
      } else {
              setNameValidation({ isValid: true, message: 'Company name is available.' });
              
              // Find similar names for suggestions
              const similar = response.data.companies?.filter(company => {
                const inputName = name.trim().toLowerCase();
                const companyName = company.name.toLowerCase();
                
                // Split into words for better matching
                const inputWords = inputName.split(/\s+/).filter(word => word.length > 0);
                const companyWords = companyName.split(/\s+/).filter(word => word.length > 0);
                
                // Check for similar names using various methods
                const isSimilar = (
                  // 1. Exact substring match (either direction)
                  companyName.includes(inputName) || inputName.includes(companyName) ||
                  
                  // 2. Word-based matching (any word from input matches any word from company)
                  inputWords.some(inputWord => 
                    inputWord.length >= 3 && companyWords.some(companyWord => 
                      companyWord.includes(inputWord) || inputWord.includes(companyWord)
                    )
                  ) ||
                  
                  // 3. Company words match input words
                  companyWords.some(companyWord => 
                    companyWord.length >= 3 && inputWords.some(inputWord => 
                      inputWord.includes(companyWord) || companyWord.includes(inputWord)
                    )
                  ) ||
                  
                  // 4. Length-based similarity for short names
                  (Math.abs(companyName.length - inputName.length) <= 3 &&
                   (companyName.includes(inputName.substring(0, Math.max(2, inputName.length - 1))) ||
                    inputName.includes(companyName.substring(0, Math.max(2, companyName.length - 1))))) ||
                  
                  // 5. Common prefix matching (for cases like "Petes" vs "Petes Ent")
                  inputWords.some(inputWord => 
                    inputWord.length >= 3 && companyName.startsWith(inputWord)
                  ) ||
                  companyWords.some(companyWord => 
                    companyWord.length >= 3 && inputName.startsWith(companyWord)
                  )
                );
                
                // Debug logging for troubleshooting
                if (inputName.includes('petes') || companyName.includes('petes')) {
                  console.log('Similarity check:', {
                    inputName,
                    companyName,
                    inputWords,
                    companyWords,
                    isSimilar,
                    substringMatch: companyName.includes(inputName) || inputName.includes(companyName),
                    wordMatch: inputWords.some(inputWord => 
                      inputWord.length >= 3 && companyWords.some(companyWord => 
                        companyWord.includes(inputWord) || inputWord.includes(companyWord)
                      )
                    ),
                    prefixMatch: inputWords.some(inputWord => 
                      inputWord.length >= 3 && companyName.startsWith(inputWord)
                    ) || companyWords.some(companyWord => 
                      companyWord.length >= 3 && inputName.startsWith(companyWord)
                    )
                  });
                }
                
                return isSimilar;
              }).slice(0, 3); // Limit to 3 suggestions
              
              setSimilarNames(similar || []);
            }
    } catch (err) {
            console.error('Error checking company name:', err);
            // Don't show error for validation check failures
    } finally {
            setCheckingName(false);
          }
        }, 500); // 500ms debounce
      };
    }, [company]),
    [company]
  );

  // Check for duplicates when name changes
  React.useEffect(() => {
    if (formData.name) {
      checkDuplicateName(formData.name);
    }
  }, [formData.name, checkDuplicateName]);

  const handleNestedChange = (parentField, childField, value) => {
    setFormData(prev => ({
      ...prev,
      [parentField]: {
        ...prev[parentField],
        [childField]: value
      }
    }));
    setHasUnsavedChanges(true);
  };

  const handleArrayChange = (field, value, action = 'toggle') => {
    setFormData(prev => {
      const currentArray = prev[field] || [];
      let newArray;
      
      if (action === 'toggle') {
        newArray = currentArray.includes(value)
          ? currentArray.filter(item => item !== value)
          : [...currentArray, value];
      } else if (action === 'add') {
        newArray = [...currentArray, value];
      } else if (action === 'remove') {
        newArray = currentArray.filter(item => item !== value);
      }
      
      return {
      ...prev,
        [field]: newArray
      };
    });
    setHasUnsavedChanges(true);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
        ...prev,
      [section]: !prev[section]
    }));
  };

  const handleKeyDown = (e) => {
    console.log('handleKeyDown called:', e.key, e.target.name, e.target.tagName);
    // Prevent form submission on Enter key for all input fields
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      
      // Only allow Enter in textarea for multi-line input
      if (e.target.name === 'initial_notes') {
        return; // Allow Enter in textarea
      }
      
      // For all other input fields, focus the next field instead
      const form = e.target.form;
      if (form) {
        const index = Array.from(form.elements).indexOf(e.target);
        const nextField = form.elements[index + 1];
        if (nextField && nextField.type !== 'submit') {
          nextField.focus();
        }
      }
    }
  };

  const handleSubmit = async (e) => {
    console.log('handleSubmit called!', e);
    e.preventDefault();
    e.stopPropagation();
    
    // Additional check to prevent unintended submissions
    if (loading) {
      console.log('Preventing submission while loading');
      return;
    }
    
    // Clear any previous errors
    setError('');
    
    // Basic validation
    if (!formData.name.trim()) {
      setError('Company name is required');
      return;
    }
    
    // Check for duplicate name validation
    if (!nameValidation.isValid) {
      setError(nameValidation.message);
      return;
    }
    
    // Additional validation
    if (formData.email && !formData.email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }
    
    if (formData.website && !formData.website.startsWith('http')) {
      setError('Website should start with http:// or https://');
      return;
    }
    
    setLoading(true);

    try {
      console.log('Form data before submission:', formData);
      
      // Clean up the data before submission
      const cleanedData = { ...formData };
      
      if (isEditing) {
        // For editing, only send fields that have values or are required
        const submitData = {};
        
        // Always include required fields
        submitData.name = cleanedData.name;
        
        // Include fields that have values
        if (cleanedData.website) submitData.website = cleanedData.website;
        if (cleanedData.industry) submitData.industry = cleanedData.industry;
        if (cleanedData.location) submitData.location = cleanedData.location;
        if (cleanedData.years_in_business) submitData.years_in_business = parseInt(cleanedData.years_in_business, 10);
        if (cleanedData.company_size) submitData.company_size = cleanedData.company_size;
        if (cleanedData.timezone) submitData.timezone = cleanedData.timezone;
        if (cleanedData.working_hours) submitData.working_hours = cleanedData.working_hours;
        if (cleanedData.phone) submitData.phone = cleanedData.phone;
        if (cleanedData.email) submitData.email = cleanedData.email;
        if (cleanedData.initial_notes) submitData.initial_notes = cleanedData.initial_notes;
        if (cleanedData.assigned_to) submitData.assigned_to = cleanedData.assigned_to;
        if (cleanedData.team_size) submitData.team_size = parseInt(cleanedData.team_size, 10);
        if (cleanedData.current_capacity) submitData.current_capacity = cleanedData.current_capacity;
        if (cleanedData.scalability) submitData.scalability = cleanedData.scalability;
        if (cleanedData.response_time) submitData.response_time = cleanedData.response_time;
        if (cleanedData.communication_style) submitData.communication_style = cleanedData.communication_style;
        if (cleanedData.availability_for_calls) submitData.availability_for_calls = cleanedData.availability_for_calls;
        if (cleanedData.minimum_engagement) submitData.minimum_engagement = cleanedData.minimum_engagement;
        if (cleanedData.travel_policy) submitData.travel_policy = cleanedData.travel_policy;
        if (cleanedData.payment_terms) submitData.payment_terms = cleanedData.payment_terms;
        if (cleanedData.invoicing_format) submitData.invoicing_format = cleanedData.invoicing_format;
        if (cleanedData.currency) submitData.currency = cleanedData.currency;
        if (cleanedData.tax_id) submitData.tax_id = cleanedData.tax_id;
        
        // Always include boolean fields
        submitData.willing_to_partner = cleanedData.willing_to_partner;
        
        // IMPORTANT: Preserve the current status to prevent reset
        if (company.partner_status?.current_status) {
          submitData.status = company.partner_status.current_status;
        }
        
        // Convert arrays and objects to JSON strings for API
        submitData.contact_persons = JSON.stringify(cleanedData.contact_persons);
        submitData.available_skills = JSON.stringify(cleanedData.available_skills);
        submitData.future_skills = JSON.stringify(cleanedData.future_skills);
        submitData.tags = JSON.stringify(cleanedData.tags);
        submitData.office_locations = JSON.stringify(cleanedData.office_locations);
        submitData.language_proficiency = JSON.stringify(cleanedData.language_proficiency);
        submitData.certifications = JSON.stringify(cleanedData.certifications);
        submitData.portfolio_links = JSON.stringify(cleanedData.portfolio_links);
        submitData.primary_contact = JSON.stringify(cleanedData.primary_contact);
        submitData.decision_maker = JSON.stringify(cleanedData.decision_maker);
        submitData.preferred_tools = JSON.stringify(cleanedData.preferred_tools);
        submitData.technology_stack = JSON.stringify(cleanedData.technology_stack);
        submitData.skill_categories = JSON.stringify(cleanedData.skill_categories);
        submitData.experience_matrix = JSON.stringify(cleanedData.experience_matrix);
        submitData.specializations = JSON.stringify(cleanedData.specializations);
        submitData.delivery_model = JSON.stringify(cleanedData.delivery_model);
        submitData.project_types = JSON.stringify(cleanedData.project_types);
        submitData.size_range = JSON.stringify(cleanedData.size_range);
        submitData.methodology = JSON.stringify(cleanedData.methodology);
        submitData.qa_processes = JSON.stringify(cleanedData.qa_processes);
        submitData.rate_ranges = JSON.stringify(cleanedData.rate_ranges);
        submitData.contract_preferences = JSON.stringify(cleanedData.contract_preferences);
        
        console.log('Edit submit data:', submitData);
        const response = await api.put(`/api/partner-companies/${company.id}`, submitData);
        console.log('Response:', response.data);
        
        if (onSuccess) {
          setHasUnsavedChanges(false);
          onSuccess(response.data);
        }
      } else {
        // For creating new company, provide default values for required fields
        if (!cleanedData.communication_style) cleanedData.communication_style = 'formal';
        if (!cleanedData.team_size) cleanedData.team_size = 1;
        if (!cleanedData.current_capacity) cleanedData.current_capacity = 'free';
        if (!cleanedData.response_time) cleanedData.response_time = 'immediate';
        
        // Convert team_size to number
        cleanedData.team_size = parseInt(cleanedData.team_size, 10);
        
        const submitData = {
          ...cleanedData,
          // Convert arrays and objects to JSON strings for API
          contact_persons: JSON.stringify(cleanedData.contact_persons),
          available_skills: JSON.stringify(cleanedData.available_skills),
          future_skills: JSON.stringify(cleanedData.future_skills),
          tags: JSON.stringify(cleanedData.tags),
          office_locations: JSON.stringify(cleanedData.office_locations),
          language_proficiency: JSON.stringify(cleanedData.language_proficiency),
          certifications: JSON.stringify(cleanedData.certifications),
          portfolio_links: JSON.stringify(cleanedData.portfolio_links),
          primary_contact: JSON.stringify(cleanedData.primary_contact),
          decision_maker: JSON.stringify(cleanedData.decision_maker),
          preferred_tools: JSON.stringify(cleanedData.preferred_tools),
          technology_stack: JSON.stringify(cleanedData.technology_stack),
          skill_categories: JSON.stringify(cleanedData.skill_categories),
          experience_matrix: JSON.stringify(cleanedData.experience_matrix),
          specializations: JSON.stringify(cleanedData.specializations),
          delivery_model: JSON.stringify(cleanedData.delivery_model),
          project_types: JSON.stringify(cleanedData.project_types),
          size_range: JSON.stringify(cleanedData.size_range),
          methodology: JSON.stringify(cleanedData.methodology),
          qa_processes: JSON.stringify(cleanedData.qa_processes),
          rate_ranges: JSON.stringify(cleanedData.rate_ranges),
          contract_preferences: JSON.stringify(cleanedData.contract_preferences)
        };

        console.log('Create submit data:', submitData);
        const response = await api.post('/api/partner-companies', submitData);
        console.log('Response:', response.data);
        
        if (onSuccess) {
          setHasUnsavedChanges(false);
          onSuccess(response.data);
        }
      }
    } catch (err) {
      console.error('Error submitting form:', err);
      
      // Handle different types of errors
      if (err.response?.data?.errors) {
        // Validation errors from API
        const errorMessages = err.response.data.errors.map(e => e.msg).join(', ');
        setError(`Validation errors: ${errorMessages}`);
      } else if (err.response?.data?.error) {
        // General API error
        if (err.response.status === 409) {
          // Duplicate company error
          setError(err.response.data.details || err.response.data.error);
        } else {
          setError(err.response.data.error);
        }
      } else if (err.message) {
        // Network or other errors
        setError(`Network error: ${err.message}`);
      } else {
        setError('Failed to save partner company. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleTag = (tagId) => {
    console.log('=== TAG CLICK EVENT ===');
    console.log('Tag clicked:', tagId);
    console.log('Current tags before:', formData.tags);
    console.log('Tag already selected:', formData.tags.includes(tagId));
    
    setFormData(prev => {
      const newTags = prev.tags.includes(tagId)
        ? prev.tags.filter(id => id !== tagId)
        : [...prev.tags, tagId];
      console.log('New tags after:', newTags);
      return {
        ...prev,
        tags: newTags
      };
    });
    setHasUnsavedChanges(true);
  };

  const renderMultiSelect = (field, options, label, placeholder, loading = false) => (
    <div className="form-group">
      <label>{label}</label>
      <div className="multi-select-container">
        {loading ? (
          <div style={{ padding: '8px 12px', color: '#6b7280', fontStyle: 'italic' }}>
            Loading options...
          </div>
        ) : options.length === 0 ? (
          <div style={{ padding: '8px 12px', color: '#6b7280', fontStyle: 'italic' }}>
            No options available
          </div>
        ) : (
          options.map(option => {
            const isSelected = formData[field]?.includes(option);
            return (
              <div
                key={option}
                onClick={() => handleArrayChange(field, option)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    handleArrayChange(field, option);
                  }
                }}
                tabIndex={0}
                role="button"
                aria-label={`Select ${option}`}
                style={{
                  padding: '8px 12px',
                  backgroundColor: isSelected ? '#3b82f6' : '#f3f4f6',
                  color: isSelected ? 'white' : '#374151',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  border: '1px solid #d1d5db',
                  fontSize: '0.875rem',
                  transition: 'all 0.2s ease',
                  userSelect: 'none'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                {option}
              </div>
            );
          })
        )}
      </div>
    </div>
  );

  const renderSectionHeader = (section, title, icon) => (
    <div 
      className="section-header" 
      onClick={() => toggleSection(section)}
      onKeyDown={(e) => {
        console.log('Section header onKeyDown:', e.key, e.target);
        if (e.key === 'Enter' || e.key === ' ') {
          console.log('Section header handling Enter/Space');
          e.preventDefault();
          e.stopPropagation();
          toggleSection(section);
        }
      }}
      tabIndex={0}
      role="button"
      aria-expanded={expandedSections[section]}
      aria-label={`Toggle ${title} section`}
    >
      <div className="section-title">
        {icon}
        <h3>{title}</h3>
      </div>
      {expandedSections[section] ? <FeatherChevronLeft size={20} /> : <FeatherChevronRight size={20} />}
    </div>
  );

  return (
    <div 
      className="partner-company-form"
      onKeyDown={(e) => {
        // Prevent any keyboard events from bubbling up to parent
        if (e.key === 'Enter') {
          console.log('PartnerCompanyForm container preventing Enter');
          e.stopPropagation();
        }
      }}
    >
      <div className="form-header">
        <h2>{isEditing ? 'Edit Partner Company' : 'Add New Partner Company'}</h2>
        <Button
          variant="neutral-tertiary"
          icon={<FeatherX />}
          onClick={() => {
            if (hasUnsavedChanges && !loading) {
              if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
                onCancel();
              }
            } else {
              onCancel();
            }
          }}
        />
      </div>

      {error && (
        <div className="error-message">
          <p>{error}</p>
          <Button
            variant="neutral-tertiary"
            size="sm"
            onClick={() => setError('')}
          >
            Dismiss
          </Button>
        </div>
      )}

      <form 
        onSubmit={handleSubmit} 
        onKeyDown={(e) => {
          console.log('Form onKeyDown:', e.key, e.target.tagName, e.target.className);
          // Prevent form submission on Enter for non-input elements
          if (e.key === 'Enter' && e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA' && e.target.tagName !== 'SELECT') {
            console.log('Preventing form submission on Enter for:', e.target);
            e.preventDefault();
            e.stopPropagation();
          }
        }}
        onKeyPress={(e) => {
          // Additional prevention for keypress events
          if (e.key === 'Enter') {
            console.log('Form onKeyPress preventing Enter');
            e.preventDefault();
            e.stopPropagation();
          }
        }}
        style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}
      >
        <div className="form-sections">
          
          {/* Section 1: Company Profile */}
          <div className="form-section">
            {renderSectionHeader('companyProfile', 'Company Profile', <FeatherGlobe size={20} />)}
            {expandedSections.companyProfile && (
              <div className="section-content">
            <div className="form-grid">
                  <div className="text-field">
                    <label className="text-field-label">Company Name *</label>
                    <div className="input-with-validation">
                      <input
                        name="company_name"
                        id="company_name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                        onKeyDown={handleKeyDown}
                  required
                  placeholder="Enter company name"
                        autoComplete="off"
                        className={`text-field-input ${!nameValidation.isValid ? 'error' : nameValidation.isValid && nameValidation.message ? 'success' : ''}`}
                      />
                      {checkingName && (
                        <div className="validation-indicator checking">
                          <span>Checking...</span>
                        </div>
                      )}
                      {!checkingName && nameValidation.message && (
                        <div className={`validation-indicator ${nameValidation.isValid ? 'success' : 'error'}`}>
                          <span>{nameValidation.message}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Similar names suggestions */}
                    {!checkingName && similarNames.length > 0 && nameValidation.isValid && (
                      <div className="similar-names-suggestions">
                        <div className="suggestions-header">
                          <span className="suggestions-icon">💡</span>
                          <span className="suggestions-text">Similar companies found:</span>
                        </div>
                        <div className="suggestions-list">
                          {similarNames.map((company, index) => (
                                                          <div key={company.id} className="suggestion-item">
                                <span className="suggestion-company-name">{company.name}</span>
                                <span className="form-status-badge">{company.partner_status?.current_status || 'No status'}</span>
                              </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  
              <TextField label="Website">
                <TextField.Input
                      name="company_website"
                      id="company_website"
                  value={formData.website}
                  onChange={(e) => handleInputChange('website', e.target.value)}
                      onKeyDown={handleKeyDown}
                  placeholder="https://example.com"
                  type="url"
                      autoComplete="off"
                />
              </TextField>
                  
              <TextField label="Industry">
                <TextField.Input
                      name="company_industry"
                      id="company_industry"
                  value={formData.industry}
                  onChange={(e) => handleInputChange('industry', e.target.value)}
                      onKeyDown={handleKeyDown}
                  placeholder="Technology, Healthcare, etc."
                      autoComplete="off"
                />
              </TextField>
                  
                  <TextField label="Location">
                    <TextField.Input
                      name="company_location"
                      id="company_location"
                      value={formData.location}
                      onChange={(e) => handleInputChange('location', e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="City, State/Country"
                      autoComplete="off"
                    />
                  </TextField>
                  
                  <TextField label="Company Email">
                    <TextField.Input
                      name="company_email"
                      id="company_email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="contact@company.com"
                      type="email"
                      autoComplete="off"
                    />
                  </TextField>
                  
                  <TextField label="Company Phone">
                    <TextField.Input
                      name="company_phone"
                      id="company_phone"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      placeholder="+1 (555) 123-4567"
                      autoComplete="off"
                    />
                  </TextField>
                  
                  <TextField label="Years in Business">
                    <TextField.Input
                      value={formData.years_in_business}
                      onChange={(e) => handleInputChange('years_in_business', e.target.value)}
                      type="number"
                      min="0"
                      max="100"
                      placeholder="5"
                    />
                  </TextField>
                  
                  <div className="partner-form-group">
                    <label>Company Size</label>
                    <select
                      name="company_size"
                      id="company_size"
                      value={formData.company_size}
                      onChange={(e) => handleInputChange('company_size', e.target.value)}
                      className="partner-form-select"
                      disabled={companySizeLoading}
                    >
                      <option value="">{companySizeLoading ? 'Loading...' : 'Select size'}</option>
                      {!companySizeLoading && companySizeOptions.map(size => (
                        <option key={size} value={size}>{size}</option>
                      ))}
                    </select>
                  </div>
                  
                  <TextField label="Timezone">
                    <TextField.Input
                      value={formData.timezone}
                      onChange={(e) => handleInputChange('timezone', e.target.value)}
                      placeholder="EST, PST, GMT, etc."
                    />
                  </TextField>
                  
                  <TextField label="Working Hours">
                    <TextField.Input
                      value={formData.working_hours}
                      onChange={(e) => handleInputChange('working_hours', e.target.value)}
                      placeholder="9 AM - 6 PM EST"
                    />
                  </TextField>
                  
                  <div className="partner-form-group">
                <label>Assigned To</label>
                <select
                      name="assigned_to"
                      id="assigned_to"
                  value={formData.assigned_to}
                  onChange={(e) => handleInputChange('assigned_to', e.target.value)}
                      className="partner-form-select"
                >
                  <option value="">Select User</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.display_name || user.email}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="checkbox-group">
              <label className="checkbox-label">
                <input
                  name="willing_to_partner"
                  id="willing_to_partner"
                  type="checkbox"
                  checked={formData.willing_to_partner}
                  onChange={(e) => handleInputChange('willing_to_partner', e.target.checked)}
                />
                Willing to Work
              </label>
              <p className="checkbox-description">
                Indicates whether this company is ready to start working (shows "Willing to Work" badge on cards)
              </p>
            </div>
                
                {renderMultiSelect('language_proficiency', ['English', 'Spanish', 'French', 'German', 'Portuguese', 'Chinese', 'Japanese', 'Korean'], 'Language Proficiency')}
                {renderMultiSelect('certifications', ['AWS', 'Azure', 'GCP', 'PMP', 'Scrum Master', 'Agile', 'ISO 27001', 'SOC 2'], 'Certifications')}
              </div>
            )}
          </div>

          {/* Section 2: Key Contacts */}
          <div className="form-section">
            {renderSectionHeader('keyContacts', 'Key Contacts', <FeatherUser size={20} />)}
            {expandedSections.keyContacts && (
              <div className="section-content">
            <div className="form-grid">
                  <div className="contact-group">
                    <h4>Primary Contact</h4>
                    <div className="form-grid">
                      <TextField label="Name">
                        <TextField.Input
                          name="primary_contact_name"
                          id="primary_contact_name"
                          value={formData.primary_contact.name}
                          onChange={(e) => handleNestedChange('primary_contact', 'name', e.target.value)}
                          placeholder="John Doe"
                          autoComplete="off"
                        />
                      </TextField>
                      <TextField label="Role">
                        <TextField.Input
                          name="primary_contact_role"
                          id="primary_contact_role"
                          value={formData.primary_contact.role}
                          onChange={(e) => handleNestedChange('primary_contact', 'role', e.target.value)}
                          placeholder="Project Manager"
                          autoComplete="off"
                        />
                      </TextField>
              <TextField label="Email">
                <TextField.Input
                          name="primary_contact_email"
                          id="primary_contact_email"
                          value={formData.primary_contact.email}
                          onChange={(e) => handleNestedChange('primary_contact', 'email', e.target.value)}
                  type="email"
                          placeholder="john@company.com"
                          autoComplete="off"
                />
              </TextField>
              <TextField label="Phone">
                <TextField.Input
                          name="primary_contact_phone"
                          id="primary_contact_phone"
                          value={formData.primary_contact.phone}
                          onChange={(e) => handleNestedChange('primary_contact', 'phone', e.target.value)}
                  placeholder="+1 (555) 123-4567"
                          autoComplete="off"
                />
              </TextField>
            </div>
                  </div>
                  
                  <div className="contact-group">
                    <h4>Decision Maker</h4>
                <div className="form-grid">
                  <TextField label="Name">
                    <TextField.Input
                          name="decision_maker_name"
                          id="decision_maker_name"
                          value={formData.decision_maker.name}
                          onChange={(e) => handleNestedChange('decision_maker', 'name', e.target.value)}
                          placeholder="Jane Smith"
                          autoComplete="off"
                    />
                  </TextField>
                  <TextField label="Role">
                    <TextField.Input
                          name="decision_maker_role"
                          id="decision_maker_role"
                          value={formData.decision_maker.role}
                          onChange={(e) => handleNestedChange('decision_maker', 'role', e.target.value)}
                          placeholder="CTO"
                          autoComplete="off"
                    />
                  </TextField>
                  <TextField label="Email">
                    <TextField.Input
                          name="decision_maker_email"
                          id="decision_maker_email"
                          value={formData.decision_maker.email}
                          onChange={(e) => handleNestedChange('decision_maker', 'email', e.target.value)}
                      type="email"
                          placeholder="jane@company.com"
                          autoComplete="off"
                    />
                  </TextField>
                    </div>
                  </div>
                </div>
                
                <div className="partner-form-group">
                  <label>Communication Style</label>
                  <select
                    name="communication_style"
                    id="communication_style"
                    value={formData.communication_style}
                    onChange={(e) => handleInputChange('communication_style', e.target.value)}
                    className="partner-form-select"
                    disabled={communicationStyleLoading}
                  >
                    <option value="">{communicationStyleLoading ? 'Loading...' : 'Select style'}</option>
                    {!communicationStyleLoading && communicationStyleOptions.map(style => (
                      <option key={style} value={style}>{style}</option>
                    ))}
                  </select>
                </div>
                
                {renderMultiSelect('preferred_tools', toolOptions, 'Preferred Communication Tools', null, toolLoading)}
                
                <TextField label="Availability for Calls">
                    <TextField.Input
                    value={formData.availability_for_calls}
                    onChange={(e) => handleInputChange('availability_for_calls', e.target.value)}
                    placeholder="Weekdays 2-4 PM EST"
                    />
                  </TextField>
                </div>
            )}
              </div>

          {/* Section 3: Technical Capabilities */}
          <div className="form-section">
            {renderSectionHeader('technicalCapabilities', 'Technical Capabilities', <FeatherGrid size={20} />)}
            {expandedSections.technicalCapabilities && (
              <div className="section-content">
                {renderMultiSelect('technology_stack', technologyOptions, 'Technology Stack', null, technologyLoading)}
                {renderMultiSelect('skill_categories', skillCategoryOptions, 'Skill Categories', null, skillCategoryLoading)}
                {renderMultiSelect('specializations', ['E-commerce', 'Fintech', 'Healthcare', 'EdTech', 'IoT', 'Blockchain', 'AI/ML', 'Cloud Migration'], 'Specializations')}
                {renderMultiSelect('delivery_model', deliveryModelOptions, 'Delivery Model', null, deliveryModelLoading)}
            </div>
            )}
          </div>

          {/* Section 4: Resource Availability */}
          <div className="form-section">
            {renderSectionHeader('resourceAvailability', 'Resource Availability', <FeatherUsers size={20} />)}
            {expandedSections.resourceAvailability && (
              <div className="section-content">
                <div className="form-grid">
                  <TextField label="Team Size">
                    <TextField.Input
                      value={formData.team_size}
                      onChange={(e) => handleInputChange('team_size', e.target.value)}
                      type="number"
                      min="1"
                      max="1000"
                      placeholder="25"
                    />
                  </TextField>
                  
                  <div className="partner-form-group">
                    <label>Current Capacity</label>
                    <select
                      value={formData.current_capacity}
                      onChange={(e) => handleInputChange('current_capacity', e.target.value)}
                      className="partner-form-select"
                      disabled={capacityLoading}
                    >
                      <option value="">{capacityLoading ? 'Loading...' : 'Select capacity'}</option>
                      {!capacityLoading && capacityOptions.map(capacity => (
                        <option key={capacity} value={capacity}>
                          {capacity === 'free' ? 'Free' : 
                           capacity === 'partial' ? 'Partially Available' : 'Fully Booked'}
                        </option>
                      ))}
                    </select>
            </div>

                  <div className="partner-form-group">
                    <label>Response Time</label>
                    <select
                      value={formData.response_time}
                      onChange={(e) => handleInputChange('response_time', e.target.value)}
                      className="partner-form-select"
                      disabled={responseTimeLoading}
                    >
                      <option value="">{responseTimeLoading ? 'Loading...' : 'Select response time'}</option>
                      {!responseTimeLoading && responseTimeOptions.map(time => (
                        <option key={time} value={time}>
                          {time === 'immediate' ? 'Immediate' : 
                           time === '1-2_weeks' ? '1-2 Weeks' : '1+ Month'}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              
                <TextField label="Scalability">
                    <TextField.Input
                    value={formData.scalability}
                    onChange={(e) => handleInputChange('scalability', e.target.value)}
                    placeholder="Can scale up to 50 developers within 2 weeks"
                    />
                  </TextField>
              </div>
            )}
          </div>

          {/* Section 5: Project Delivery Capability */}
          <div className="form-section">
            {renderSectionHeader('projectDelivery', 'Project Delivery Capability', <FeatherFileText size={20} />)}
            {expandedSections.projectDelivery && (
              <div className="section-content">
                {renderMultiSelect('project_types', projectTypeOptions, 'Project Types', null, projectTypeLoading)}
                {renderMultiSelect('size_range', sizeRangeOptions, 'Project Size Range', null, sizeRangeLoading)}
                {renderMultiSelect('methodology', methodologyOptions, 'Methodology', null, methodologyLoading)}
                {renderMultiSelect('qa_processes', qaProcessOptions, 'QA Processes', null, qaProcessLoading)}
                
                <div className="form-grid">
                  <TextField label="Minimum Engagement">
                    <TextField.Input
                      value={formData.minimum_engagement}
                      onChange={(e) => handleInputChange('minimum_engagement', e.target.value)}
                      placeholder="40 hours per week"
                    />
                  </TextField>
                  
                  <TextField label="Travel Policy">
                    <TextField.Input
                      value={formData.travel_policy}
                      onChange={(e) => handleInputChange('travel_policy', e.target.value)}
                      placeholder="Available for on-site work"
                    />
                  </TextField>
                </div>
              </div>
            )}
            </div>

          {/* Section 6: Financial Terms */}
          <div className="form-section">
            {renderSectionHeader('financialTerms', 'Financial Terms', <FeatherTrendingUp size={20} />)}
            {expandedSections.financialTerms && (
              <div className="section-content">
                {renderMultiSelect('contract_preferences', contractPreferenceOptions, 'Contract Preferences', null, contractPreferenceLoading)}
                
                <div className="form-grid">
                  <TextField label="Payment Terms">
                    <TextField.Input
                      value={formData.payment_terms}
                      onChange={(e) => handleInputChange('payment_terms', e.target.value)}
                      placeholder="Net 30"
                    />
                  </TextField>
                  
                  <TextField label="Invoicing Format">
                    <TextField.Input
                      value={formData.invoicing_format}
                      onChange={(e) => handleInputChange('invoicing_format', e.target.value)}
                      placeholder="Monthly, milestone-based"
                    />
                  </TextField>
                  
                  <div className="partner-form-group">
                    <label>Currency</label>
                    <select
                      value={formData.currency}
                      onChange={(e) => handleInputChange('currency', e.target.value)}
                      className="partner-form-select"
                      disabled={currencyLoading}
                    >
                      {currencyLoading ? (
                        <option value="">Loading...</option>
                      ) : (
                        currencyOptions.map(currency => (
                          <option key={currency} value={currency}>{currency}</option>
                        ))
                      )}
                    </select>
                  </div>
                  
                  <TextField label="Tax ID">
                    <TextField.Input
                      value={formData.tax_id}
                      onChange={(e) => handleInputChange('tax_id', e.target.value)}
                      placeholder="Tax identification number"
                    />
                  </TextField>
                </div>
              </div>
            )}
            </div>



          {/* Tags */}
          <div className="form-section">
            {renderSectionHeader('tags', 'Tags', <FeatherTag size={20} />)}
            {expandedSections.tags && (
              <div className="section-content">
            <div className="tags-container">
              {console.log('Available tags:', availableTags)}
              {console.log('Selected tags:', formData.tags)}
              {availableTags.length === 0 ? (
                <p>No tags available. Loading...</p>
              ) : (
                availableTags.map(tag => {
                  const isSelected = formData.tags.includes(tag.id);
                  console.log('Rendering tag:', tag, 'Selected:', isSelected);
                  return (
                <Badge
                  key={tag.id}
                      variant={isSelected ? "brand-primary" : "neutral-tertiary"}
                  onClick={() => toggleTag(tag.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleTag(tag.id);
                        }
                      }}
                  className="clickable-tag"
                      tabIndex={0}
                      role="button"
                      aria-label={`Toggle tag ${tag.name}`}
                  style={{ 
                        backgroundColor: isSelected ? tag.color : tag.color + '20',
                        color: isSelected ? 'white' : tag.color,
                        border: isSelected ? `2px solid ${tag.color}` : '1px solid transparent'
                  }}
                >
                  <FeatherTag size={12} />
                  {tag.name}
                      {isSelected && <span style={{ marginLeft: '4px' }}>✓</span>}
                </Badge>
                  );
                })
              )}
            </div>
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="form-section">
            {renderSectionHeader('notes', 'Notes', <FeatherFileText size={20} />)}
            {expandedSections.notes && (
              <div className="section-content">
                <TextArea label="Initial Notes">
                  <TextArea.Input
              value={formData.initial_notes}
                    onChange={(e) => handleInputChange('initial_notes', e.target.value)}
              placeholder="Enter any additional notes about this company..."
              rows={4}
            />
                </TextArea>
              </div>
            )}
          </div>
        </div>

        <div className="form-actions">
          <Button
            variant="neutral-tertiary"
            onClick={() => {
              if (hasUnsavedChanges && !loading) {
                if (window.confirm('You have unsaved changes. Are you sure you want to cancel?')) {
                  onCancel();
                }
              } else {
                onCancel();
              }
            }}
            disabled={loading}
          >
            Cancel
          </Button>
          {onSwitchToQuick && (
            <Button
              type="button"
              variant="neutral-secondary"
              onClick={onSwitchToQuick}
              disabled={loading}
            >
              Switch to Quick Mode
            </Button>
          )}
          <Button
            type="submit"
            variant="brand-primary"
            icon={<FeatherSave />}
            loading={loading}
            disabled={loading}
          >
            {loading ? (isEditing ? 'Updating...' : 'Creating...') : (isEditing ? 'Update Company' : 'Create Company')}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default PartnerCompanyForm; 